import { Link } from "react-router-dom";
import { ArrowRight, Award, Shield, Clock, Users, CheckCircle, Star, Phone, Target, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";


const values = [
  { icon: Award, title: "Excellence", desc: "We hold ourselves to the highest standards in every project, no matter how big or small." },
  { icon: Shield, title: "Integrity", desc: "Transparent pricing with no hidden fees. What we quote is what you pay." },
  { icon: Clock, title: "Reliability", desc: "We show up on time, every time, and complete projects when promised." },
  { icon: Users, title: "Customer Focus", desc: "Your satisfaction isn't just a goal—it's our guarantee." },
];

const stats = [
  { num: "15+", label: "Years in Business" },
  { num: "560+", label: "Projects Completed" },
  { num: "50+", label: "Brand Partnerships" },
  { num: "98%", label: "Satisfaction Rate" },
];

const whyChooseUs = [
  "Licensed, bonded, and fully insured",
  "Background-checked professional technicians",
  "Upfront pricing with no surprise fees",
  "Quality materials and professional-grade tools",
  "Clean worksite - we leave your home spotless",
  "Warranty on all workmanship",
  "Flexible scheduling including weekends",
  "Free estimates on all projects"
];

export const AboutPage = () => {
  return (
    <div className="pt-24 bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="badge-orange mb-4">About Us</div>
              <h1 className="text-4xl lg:text-5xl font-extrabold text-[#1a1a2e] mb-6">
                Get To Know Us, Trust We're Always Ready To <span className="text-[#f97316]">Paint</span>
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                For over 15 years, Vanguard Painting Masters has been transforming homes 
                with precision painting and unwavering dedication to quality. We're not just 
                painters—we're artisans committed to making your home beautiful.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {["Friendly & Transparent", "Time & Energy Saved", "Professional Results", "Peace of Mind"].map((t) => (
                  <div key={t} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#f97316]" />
                    <span className="text-gray-700 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&q=80" alt="Professional Painter" className="w-full rounded-2xl shadow-2xl" />
              <div className="absolute -bottom-6 -left-6 stat-card">
                <div className="stat-number">15+</div>
                <p className="text-white/90 text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#1a2a44]" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={s.label} className="text-center" data-testid={`about-stat-${i}`}>
                <div className="text-5xl font-extrabold text-[#f97316] mb-2" style={{fontFamily: 'Outfit'}}>{s.num}</div>
                <p className="text-gray-400">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800&q=80" 
                alt="Craftsman at work" 
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <div className="badge-orange mb-4">Our Story</div>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-[#1a1a2e] mb-6">
                Built on Trust, Driven by Quality
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Vanguard Painting Masters was founded with a simple mission: to provide homeowners 
                with premium painting services they can trust. What started as a one-man 
                operation has grown into a full-service painting company serving communities across 
                the Lower Mainland.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Over the years, we've built our reputation one satisfied customer at a time. We believe 
                that every home deserves the same level of care and attention to detail, whether we're 
                painting a single room, refinishing cabinets, or transforming an entire home exterior.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Today, we're proud to be the go-to painting team for hundreds of families who trust us 
                to make their homes beautiful. Our commitment to quality workmanship, honest pricing, 
                and exceptional customer service remains at the heart of everything we do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-[#f97316] rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#1a1a2e] mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To deliver exceptional painting services that exceed our customers' 
                expectations. We strive to make home improvement stress-free by providing reliable, 
                professional service with transparent pricing and beautiful results.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-[#1a2a44] rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#1a1a2e] mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be the most trusted name in painting services across the Lower Mainland—known for 
                our integrity, craftsmanship, and the lasting relationships we build with every 
                homeowner we serve. We envision a community where every home is well-maintained and 
                every homeowner has a reliable partner they can call.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="badge-orange mx-auto mb-4">Our Values</div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-[#1a1a2e]">What We Stand For</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              These core values guide every decision we make and every project we undertake.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div key={v.title} className="card-service p-8 text-center" data-testid={`value-card-${i}`}>
                <div className="icon-box mx-auto mb-6">
                  <v.icon className="w-8 h-8 text-[#f97316]" />
                </div>
                <h3 className="text-xl font-bold text-[#1a1a2e] mb-3">{v.title}</h3>
                <p className="text-gray-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 bg-[#0c2340]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 bg-[#f97316] rounded-sm"></span>
                <span className="text-[#f97316] font-semibold">Why Choose Us</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-6">
                The Vanguard Difference
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                When you choose Vanguard Painting Masters, you're choosing a team that 
                treats your home with the same care and respect we'd give our own. Here's what 
                sets us apart:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyChooseUs.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#f97316] flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80" 
                alt="Professional service" 
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-quote" data-testid="why-choose-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-7 h-7 fill-white text-white" />)}
          </div>
          <h2 className="text-3xl lg:text-5xl font-extrabold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-white/90 text-lg mb-10">
            Whether you need a quick repair or a complete home transformation, we're here to help. 
            Get a free consultation and estimate today!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-[#f97316] hover:bg-gray-100 font-bold rounded-lg px-8 py-6" data-testid="about-cta-btn">
                Get Free Quote <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:2368681195">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold rounded-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" /> Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
